"use client";

import NextScript from "next/script";
import React, { useEffect } from "react";
import type { Nullable } from "../../@types/generic";
import { useCountry, useIsWebview } from "../ApplicationState/hooks";
import { useConsent, useIsPay } from "../Consent/hooks";
import { hijackDataLayerPush } from "./hijack";

const gtmConfig = {
    standalone: {
        containerId: "GTM-M5C222WD",
        auth: "oM0KW5mVtn29YdWWV1rBHg",
        preview: "env-895",
    },
    webview: {
        containerId: "GTM-N5PWM5MX",
        auth: null,
        preview: null,
    },
};

const getConfig = (
    isWebview: boolean | null
): {
    containerId: string;
    auth: Nullable<string>;
    preview: Nullable<string>;
} => {
    if (isWebview) {
        return gtmConfig.webview;
    }

    return gtmConfig.standalone;
};

export const GTM: React.FC = () => {
    const country = useCountry();
    const consent = useConsent();
    const isPay = useIsPay();
    const isWebview = useIsWebview();

    useEffect(() => {
        if (country === "pl") {
            window.addEventListener("gtm_loaded", hijackDataLayerPush);
        }

        return (): void => {
            window.removeEventListener("gtm_loaded", hijackDataLayerPush);
        };
    }, [country]);

    if (!consent || isPay) {
        return null;
    }

    const config = getConfig(isWebview);

    let eventListener = "";
    if (country === "pl") {
        eventListener = `j.addEventListener('load', function() {
              var _ge = new CustomEvent('gtm_loaded', { bubbles: true });
              d.dispatchEvent(_ge);
            });`;
    }

    const url =
        !isWebview && process.env.NEXT_PUBLIC_DEPLOY_ENV === "production"
            ? `https://${country === "ch" || country === "at" ? country : "www"}.wetter.com/metrics/`
            : // We need this url for Local and Staging urls because /metrics url asks for more scripts with relative path
              // which does not exist on those environments
              "https://www.googletagmanager.com/gtm.js";

    if (process.env.NEXT_PUBLIC_DEPLOY_ENV === "production") {
        return (
            <NextScript
                id="gtm-head"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{
                    __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            '${url}?id='+i+dl;${eventListener}f.parentNode.insertBefore(j,f);
                            })(window,document,'script','dataLayer','${config.containerId}');`,
                }}
            />
        );
    } else if (config.auth && config.preview) {
        return (
            <NextScript
                id="gtm-head"
                strategy="lazyOnload"
                dangerouslySetInnerHTML={{
                    __html: `
                            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            '${url}?id='+i+dl+ '&gtm_auth=${config.auth}&gtm_preview=${config.preview}&gtm_cookies_win=x';${eventListener}f.parentNode.insertBefore(j,f);
                            })(window,document,'script','dataLayer','${config.containerId}');`,
                }}
            />
        );
    }

    return null;
};
